import * as React from 'react';
import { SGBase, SGBaseProps, OptionalVector2, FillOptionalVector2 } from './sgbase';
import { EPanelOrigin, Panel, PanelOriginToVector2, UVs } from './panel';
import { Vector2 } from '../utils/transform';
import bind from 'bind-decorator';
import { TraversalState, BuildSgNodeReturn } from './sgproto';


declare global { namespace JSX {
	interface IntrinsicElements {
	  "vsg-panel-anchor": any;
	}
  }
}

interface PanelAnchorProps extends React.HTMLAttributes<HTMLDivElement>, SGBaseProps
{
	location?: EPanelOrigin | OptionalVector2; // Location of this panel anchor relative to its panel's center (0,0). Range: [-1,1] on x and y.
	latched?: boolean; // Whether it will retain its original position for its lifespan.
}

export class PanelAnchor extends SGBase<PanelAnchorProps, null>
{
	private m_latchedPosition: UVs = null;

	constructor( props: any )
	{
		super( props );
		super.setBuildNodeOverride( this.buildNode );
	}

	public getNodeType(): string
	{
		return "panel-anchor";
	}

	public relatch()
	{
		this.m_latchedPosition = null;
	}

	@bind private buildNode( traversalState: TraversalState, domNode: HTMLElement ): BuildSgNodeReturn
	{
		if ( !traversalState.currentPanel )
		{
			return [{ ...traversalState, bShouldAbort: true }, null];
		}

		let vecLocation: Vector2 = null;
		if ( typeof this.props.location === "object" )
		{
			vecLocation = FillOptionalVector2( this.props.location, { x: 0, y: 0 } );
		}
		else if ( typeof this.props.location === "number" )
		{
			vecLocation = PanelOriginToVector2( this.props.location );
		}

		let node = this.createSgNode( domNode );

		if ( this.props.latched && this.m_latchedPosition !== null )
		{
			node.properties["anchor-u"] = this.m_latchedPosition.u;
			node.properties["anchor-v"] = this.m_latchedPosition.v;
		}
		else if ( vecLocation )
		{
			// Explicit location within either an external panel or an internal subpanel.

			const localAnchorUVs: UVs = {
				u: vecLocation.x *  0.5 + 0.5,
				v: vecLocation.y * -0.5 + 0.5,
			};

			const globalAnchorUVs = traversalState.currentPanel.props.overlay_key
				? localAnchorUVs
				: traversalState.currentPanel.scaleLocalUVToGlobal( localAnchorUVs );

			node.properties["anchor-u"] = globalAnchorUVs.u;
			node.properties["anchor-v"] = globalAnchorUVs.v;
		}
		else
		{
			// Automatic location within this scenegraph panel based on the element's center position on the page.

			const actualDocument = domNode.ownerDocument;

			const rect = domNode.getBoundingClientRect();
			const nMidpointX = rect.left + rect.width / 2;
			const nMidpointY = rect.top + rect.height / 2;

			// If the panel anchor is scrolled outside of its panel, exclude it (and its children) from the scene graph.
			const rectPanel = traversalState.currentPanel.m_Rect;
			if ( ( nMidpointX < rectPanel.x || nMidpointX > ( rectPanel.x + rectPanel.width ) ) ||
				( nMidpointY < rectPanel.y || nMidpointY > ( rectPanel.y + rectPanel.height ) ) )
			{
				return [{ ...traversalState, bShouldAbort: true }, null];
			}

			const fWindowWidth = actualDocument.defaultView.innerWidth;
			const fWindowHeight = actualDocument.defaultView.innerHeight;

			if ( fWindowHeight > 0 && fWindowWidth > 0 )
			{
				node.properties["anchor-u"] = nMidpointX / fWindowWidth;
				node.properties["anchor-v"] = nMidpointY / fWindowHeight;
			}
			else
			{
				return [{ ...traversalState, bShouldAbort: true }, null];
			}
		}

		this.m_latchedPosition = {
			u: node.properties["anchor-u"],
			v: node.properties["anchor-v"],
		};

		return [traversalState, node];
	}
}
